class LogoSelector extends HTMLElement {
  /* The header switches layout at 700px; the desktop wordmark follows the same line. */
  static desktopQuery = window.matchMedia('(min-width: 700px)');

  constructor() {
    super();
    this.init();
  }

  connectedCallback() {
    LogoSelector.desktopQuery.addEventListener('change', this.#onBreakpointChange);
  }

  disconnectedCallback() {
    LogoSelector.desktopQuery.removeEventListener('change', this.#onBreakpointChange);
  }

  /**
   * Brand 1 swaps between its text logo and its desktop wordmark at the breakpoint,
   * so the header is redrawn when the viewport crosses it. Bound as a field so add
   * and remove see the same reference: desktopQuery is static and outlives any one
   * instance, and the theme editor re-creates this element on every header setting
   * change — a listener left behind would keep redrawing from a detached element.
   */
  #onBreakpointChange = () => {
    const active = [...this.logoLinks].findIndex((l) => l.classList.contains('active'));
    if (active >= 0) this.updateMainLogo(active);
  };

  init() {
    this.logoLinks = this.querySelectorAll('.header__left-nav-item');

    /* A brand whose tab is switched off in the header settings (e.g. a retired brand a
       returning visitor still has saved) can't stay selected, so it falls back to JAE. */
    const storedLogo = localStorage.getItem('selectedLogo');
    const isAvailable = [...this.logoLinks].some(
      (link) => link.getAttribute('data-logo') === storedLogo && link.style.display !== 'none'
    );
    const savedLogo = isAvailable ? storedLogo : 'jae';
    if (storedLogo && !isAvailable) {
      localStorage.setItem('selectedLogo', savedLogo);
    }

    this.logoLinks.forEach((link, index) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const logoType = link.getAttribute('data-logo');

        // Save selection to localStorage before navigation
        if (logoType) {
          localStorage.setItem('selectedLogo', logoType);
          localStorage.setItem('pageContext', logoType);
        }

        // Set active state immediately
        this.setActiveLink(link, index);
      });
    });




    // Remove all logo classes and apply the saved one to body
    document.body.classList.remove('jae', 'loyal', 'avalon');
    document.body.classList.add(savedLogo);

    // Find and activate the corresponding logo link
    let activeIndex = 0;
    this.logoLinks.forEach((link, index) => {
      link.classList.remove('active');
      if (link.getAttribute('data-logo') === savedLogo) {
        link.classList.add('active');
        activeIndex = index;
      }
    });

    // Update main logo to match active selection
    if (this.logoLinks[activeIndex]) {
      this.updateMainLogo(activeIndex);
    }

    // Logo will be revealed by updateMainLogo after determining which type to show
  }

  setActiveLink(activeLink, index) {
    this.logoLinks.forEach(link => {
      link.classList.remove('active');
    });
    activeLink.classList.add('active');

    // Get logo type from data-logo attribute
    const logoType = activeLink.getAttribute('data-logo') || 'jae';

    // Remove all logo classes from body and add the new one
    document.body.classList.remove('jae', 'loyal', 'avalon');
    document.body.classList.add(logoType);

    // Always navigate to the corresponding page
    const linkHref = activeLink.getAttribute('href');
    const currentPath = window.location.pathname;
    const targetPath = linkHref ? new URL(linkHref, window.location.origin).pathname : null;
    const isSamePage = targetPath && targetPath === currentPath;
    if (linkHref && linkHref !== '#' && !isSamePage) {
      window.location.href = linkHref;
      return;
    }

    this.dispatchEvent(new CustomEvent('logo-selected', {
      detail: {
        index: index,
        element: activeLink,
        alt: activeLink.querySelector('img')?.alt || '',
        src: activeLink.querySelector('img')?.src || '',
        logo: logoType
      },
      bubbles: true
    }));
  }

  updateMainLogo(logoIndex) {
    // Handle both index (h1.header__logo > a) and other pages (a.header__logo)
    const headerLogoContainer = document.querySelector('.header__logo');
    let mainLogoLink = headerLogoContainer;

    if (headerLogoContainer && headerLogoContainer.tagName === 'H1') {
      // Index page: h1.header__logo contains an <a>
      mainLogoLink = headerLogoContainer.querySelector('a');
    }
    // Else: other pages where a.header__logo is the link itself

    const footerLogoDiv = document.querySelector('.footer__logo');
    const activeNavLogo = this.logoLinks[logoIndex]?.querySelector('img');

    /**
     * Check if text logo mode is enabled
     */


    // Get configuration for current brand
    const useText1 = this.getAttribute('data-use-text-1') === 'true';
    const useText2 = this.getAttribute('data-use-text-2') === 'true';
    const useText3 = this.getAttribute('data-use-text-3') === 'true';
    const useTexts = [useText1, useText2, useText3];
    const useTextForCurrent = useTexts[logoIndex];

    if (mainLogoLink) {
      // Get logo text options from attributes
      const logoText1 = this.getAttribute('data-logo-text-1') || 'JAE';
      const logoText2 = this.getAttribute('data-logo-text-2') || 'LOYAL';
      const logoText3 = this.getAttribute('data-logo-text-3') || 'AVALON';

      const logoNames = [logoText1, logoText2, logoText3];
      const logoName = logoNames[logoIndex] || logoText1;

      /**
       * Brand 1 supplies a desktop-only wordmark. Below the header breakpoint the theme
       * keeps its text logo: the real wordmark is roughly 2.5x wider than the text that
       * stands in for it (342px vs 138px at 24px tall), so on one line it cannot fit the
       * ~140px of centred space the header leaves between the icons.
       */
      const isDesktop = LogoSelector.desktopQuery.matches;
      const suffix = isDesktop ? '' : '-mobile';
      const headerSrc =
        logoIndex === 0 ? this.getAttribute(`data-header-logo-1${suffix}`) : null;
      const headerRatio = parseFloat(this.getAttribute(`data-header-logo-1${suffix}-ratio`));
      const useWordmark = Boolean(headerSrc) && headerRatio > 0;

      if (useTextForCurrent && !useWordmark) {
        /* Use text logo */
        mainLogoLink.innerHTML = `
          <span class="header__logo-text h5 logolocal">${logoName}</span>
        `;
        setTimeout(() => {
          const logoText = mainLogoLink.querySelector('.header__logo-text');
          if (logoText) logoText.style.opacity = '1';
          // Remove fixed height after transition
          mainLogoLink.style.minHeight = '';
        }, 0);
      } else if (useWordmark || activeNavLogo) {
        /* Use image logo */
        const NAV_SRC_WIDTH = /width=\d+/;
        /* Mobile gets a shorter height: the header only leaves ~140px of centred room
           between the icons, and the CSS cap trims it further if the viewport is narrower. */
        const LOGO_DISPLAY_HEIGHT = isDesktop ? 32 : 28;

        /**
         * CSS lays the logo out at a fixed height with an auto width (see the scoped
         * <style> in sections/header.liquid), so the asset's own ratio decides how wide it
         * renders. Replaces the old fixed 183x128/125px inherited from a retired logo.
         *
         * Candidates start at 2x on purpose: the detail loss in these wordmarks comes from
         * the CDN downscaling to a 1x bitmap, not from the device pixel ratio, so offering
         * no 1x source keeps the browser from ever picking a blurry one.
         */
        const baseSrc = useWordmark ? headerSrc : activeNavLogo.src;

        /**
         * The ratio comes from the wordmark's own aspect_ratio, or from the tab thumbnail
         * for the other brands. That thumbnail may still be loading on this first pass, so
         * render immediately regardless — waiting would leave the previous brand's logo on
         * screen — and redraw once it reports its size, which tightens the srcset.
         */
        const navSized =
          Boolean(activeNavLogo) && activeNavLogo.complete && activeNavLogo.naturalWidth > 0;
        const ratio = useWordmark
          ? headerRatio
          : navSized
            ? activeNavLogo.naturalWidth / activeNavLogo.naturalHeight
            : 0;

        if (!useWordmark && !navSized && activeNavLogo && !activeNavLogo.dataset.awaitingSize) {
          activeNavLogo.dataset.awaitingSize = 'true';
          const clear = () => delete activeNavLogo.dataset.awaitingSize;
          activeNavLogo.addEventListener(
            'load',
            () => {
              clear();
              this.updateMainLogo(logoIndex);
            },
            { once: true }
          );
          /* Without this the flag would stay set forever on a failed thumbnail. */
          activeNavLogo.addEventListener('error', clear, { once: true });
        }

        /* Wide enough to cover any wordmark up to ~12:1 at the display height, so even the
           pre-ratio first paint is never upscaled. */
        const FALLBACK_WIDTH = 400;
        const width = ratio ? Math.round(LOGO_DISPLAY_HEIGHT * ratio) : 0;
        const at = (n) => baseSrc.replace(NAV_SRC_WIDTH, `width=${width ? width * n : FALLBACK_WIDTH}`);
        /* Dimensions and srcset are only meaningful once the ratio is known; a wrong pair
           would reserve the wrong box and cause a layout shift. */
        const sizing = width
          ? `srcset="${at(2)} ${width * 2}w, ${at(3)} ${width * 3}w"
                 sizes="${width}px"
                 width="${width}"
                 height="${LOGO_DISPLAY_HEIGHT}"`
          : '';
        /* The tab thumbnail's alt describes a small brand icon, not the full wordmark. */
        const altText = useWordmark ? logoName : activeNavLogo.alt;

        mainLogoLink.innerHTML = `
          <span class="sr-only">${logoName}</span>
          <img src="${at(2)}"
               alt="${altText}"
               ${sizing}
               class="header__logo-image">
        `;
        setTimeout(() => {
          const logoImage = mainLogoLink.querySelector('.header__logo-image');
          if (logoImage) logoImage.style.opacity = '1';
          // Remove fixed height after transition
          mainLogoLink.style.minHeight = '';
        }, 0);
      }
    }

    this.#switchVisible(['jae-footer-logo', 'loyal-footer-logo', 'avalon-footer-logo'], logoIndex);
    this.#switchVisible(['jae-social-media', 'loyal-social-media', 'avalon-social-media'], logoIndex);
  }

  #switchVisible(ids, activeIndex) {
    ids.forEach((id, i) => {
      const el = document.getElementById(id);
      if (el) el.style.display = i === activeIndex ? '' : 'none';
    });
  }

  setActive(index) {
    if (this.logoLinks[index]) {
      this.setActiveLink(this.logoLinks[index], index);
    }
  }

  getActive() {
    const activeLink = this.querySelector('.header__left-nav-item.active');
    if (activeLink) {
      return {
        element: activeLink,
        alt: activeLink.querySelector('img')?.alt || '',
        src: activeLink.querySelector('img')?.src || ''
      };
    }
    return null;
  }
}

customElements.define('logo-selector', LogoSelector);
